import ErrorCodes from '../constants/ErrorCodes';
import { GA_DEDUPE_MS, ROLLBAR_SESSION_BUDGET, ROLLBAR_SESSION_SAMPLE_RATE, SAGA_SUPPRESS_MS, TECHNICAL_ERROR_EVENT, TechnicalErrorChannels, TechnicalErrorReasons } from '../constants/TechnicalErrors';
const ID_SEGMENT = /^(\d+|[0-9a-f]{8,})$/i;
let deps = null;
let sampled = false;
let rollbarBudget = 0;
let unloading = false;
let lastApiReport = null;
const gaSeenAt = {};
const rollbarSeen = {};

/**
 * Which failures are worth a report. Everything not listed returns null and is dropped:
 * 401 (the apps run unauthenticated by design), 402/403/404/409/422/423 (the customer's own
 * situation), and a 429 without `retry_after` — that one is "out of credits", a billing outcome
 * already counted as `out_of_limits_message`, not a technical failure.
 */
const classify = (status, retryAfter) => {
  if (status === ErrorCodes.TOO_MANY_REQUESTS) {
    return retryAfter > 0 ? TechnicalErrorReasons.RATE_LIMIT : null;
  } else if (status === ErrorCodes.FAILED_DEPENDENCY) {
    return TechnicalErrorReasons.DEPENDENCY;
  } else if (status === ErrorCodes.REQUEST_TIMEOUT) {
    return TechnicalErrorReasons.TIMEOUT;
  } else if (status === ErrorCodes.FETCH_ERROR) {
    return TechnicalErrorReasons.NETWORK;
  } else if (status === ErrorCodes.PARSE_ERROR) {
    return TechnicalErrorReasons.PARSE;
  } else if (status >= ErrorCodes.INTERNAL_SERVER_ERROR) {
    return TechnicalErrorReasons.SERVER_ERROR;
  }
  return null;
};

/**
 * Endpoint without the host, without the query string and without ids, so that every occurrence of
 * one failure groups under one key.
 */
const normalizePath = (requestURL, apiHost) => {
  const withoutQuery = requestURL.split('?')[0];
  const path = withoutQuery.indexOf(apiHost) === 0 ? withoutQuery.slice(apiHost.length) : withoutQuery;
  const segments = path.split('/');
  const segmentCount = segments.length;
  for (let i = 0; i < segmentCount; i += 1) {
    if (ID_SEGMENT.test(segments[i])) {
      segments[i] = '#id';
    }
  }
  return segments.join('/');
};

/**
 * One occurrence → at most one GA4 event and at most one Rollbar warning.
 *
 * The two channels answer different questions and are gated differently on purpose. GA4 gets every
 * occurrence (minus the retry dedupe) because it answers "how many, whose, which plan". Rollbar is
 * sampled and capped because it only has to answer "has a failure class appeared that never existed
 * before" — the title carries the discriminators so a new class becomes a new item, and a plain
 * "new item in production" alert fires on it within a minute.
 */
const emit = report => {
  const key = `${report.channel}|${report.reason}|${report.status}|${report.path}`;
  const now = Date.now();
  if (deps.reportingEnabled === false) {
    // eslint-disable-next-line no-console
    console.warn('[tech-error]', key, report);
    return;
  }
  if (gaSeenAt[key] === undefined || now - gaSeenAt[key] > GA_DEDUPE_MS) {
    gaSeenAt[key] = now;
    deps.gtmPush({
      event: TECHNICAL_ERROR_EVENT,
      action: 'show',
      app: deps.appName,
      channel: report.channel,
      reason: report.reason,
      status: report.status,
      errorType: report.type,
      path: report.path,
      retryAfter: report.retryAfter
    });
  }
  if (sampled === false || rollbarBudget >= ROLLBAR_SESSION_BUDGET || rollbarSeen[key] === true) {
    return;
  }
  rollbarSeen[key] = true;
  rollbarBudget += 1;

  // A message string, not an Error: `errorSagas.logError` builds `new Error('[tag]')` inside
  // itself, so its message and stack are identical for every occurrence and Rollbar collapses
  // every status under one item. Putting the discriminators in the title is what makes a new
  // failure class a new item instead of one more occurrence of an old one. The error text stays
  // out of it entirely: the sources build that from the response body, and on a parse failure V8
  // quotes the first bytes of the body into `ex.toString()`.
  deps.rollbar.warning(`[tech-error] ${report.channel} ${report.status} ${report.type || report.reason} ${report.method} ${report.path}`, {
    app: deps.appName,
    channel: report.channel,
    reason: report.reason,
    method: report.method,
    path: report.path,
    status: report.status,
    type: report.type,
    retryAfter: report.retryAfter,
    durationMs: report.durationMs,
    route: window.location.pathname,
    sampleRate: deps.sampleRate
  });
};

/**
 * Reports a technical failure the customer was shown, to Rollbar as a *warning* and to GA4.
 *
 * `error` means our code broke, `warning` means something we depend on broke — a provider outage is
 * not a bug in this bundle. Created for ITRBU-659, where a SERP provider was throttled for 22 hours
 * and neither channel recorded a single customer-visible 500.
 */
class TechnicalErrorReportService {
  /**
   * Wire the reporter once per app, at import time. Until this runs, every report is a no-op.
   *
   * @param {Object}   config
   * @param {string}   config.appName           APP_CONFIG.APP_NAME
   * @param {string}   config.apiHost           APP_CONFIG.API_HOST — the only host ever reported
   * @param {boolean}  config.reportingEnabled  false outside production/staging; console.warn instead
   * @param {Object}   config.rollbar           Rollbar instance
   * @param {Function} config.gtmPush           GTMService.dataLayer.push
   * @param {number}   [config.sampleRate]      share of sessions that reach Rollbar
   */
  static configure({
    appName,
    apiHost,
    reportingEnabled,
    rollbar,
    gtmPush,
    sampleRate
  }) {
    deps = {
      appName,
      apiHost,
      reportingEnabled,
      rollbar,
      gtmPush,
      sampleRate: sampleRate === undefined ? ROLLBAR_SESSION_SAMPLE_RATE : sampleRate
    };
    sampled = Math.random() < deps.sampleRate;

    // An in-flight request rejecting because the customer navigated away looks exactly like a
    // network outage. It is the single biggest false positive for any transport-level counter.
    window.addEventListener('pagehide', () => {
      unloading = true;
    });

    // The page can come back: a bfcache restore keeps this module's state, and on iOS Safari a
    // plain tab switch fires the pair. Without this the first `pagehide` would end reporting for
    // the rest of the page's life.
    window.addEventListener('pageshow', () => {
      unloading = false;
    });
  }

  /**
   * Reports one failed API request. Called from the data sources, where the response is first
   * read and where the endpoint is still known.
   *
   * `failure.text` is deliberately not taken: the callers build it from the response body and
   * nothing may leave the browser that was not written by us.
   *
   * @param {Object}  failure
   * @param {string}  failure.requestURL
   * @param {string}  failure.method
   * @param {number}  failure.status      HTTP status, or ErrorCodes.FETCH_ERROR / PARSE_ERROR
   * @param {?string} failure.type        API error type — the exception class name
   * @param {?number} failure.retryAfter  seconds the API asked us to wait
   * @param {number}  [failure.durationMs]
   * @returns {boolean} whether anything was reported
   */
  static report({
    requestURL,
    method,
    status,
    type,
    retryAfter,
    durationMs
  }) {
    try {
      if (deps === null || unloading === true) {
        return false;
      }
      if (typeof requestURL !== 'string' || requestURL.indexOf(deps.apiHost) !== 0) {
        return false;
      }
      const reason = classify(status, retryAfter);
      if (reason === null) {
        return false;
      }

      // Offline is the customer's network, not our failure. Same test the async loader
      // boundary already applies before it reports (`AsyncLoaderErrorBoundaryContainer.tsx`).
      if (reason === TechnicalErrorReasons.NETWORK && navigator.onLine === false) {
        return false;
      }
      const path = normalizePath(requestURL, deps.apiHost);
      lastApiReport = {
        at: Date.now(),
        reason,
        status,
        path
      };
      emit({
        channel: TechnicalErrorChannels.API,
        reason,
        method,
        path,
        status,
        type,
        retryAfter,
        durationMs
      });
      return true;
    } catch (ex) {
      return false;
    }
  }

  /**
   * Reports a failure a saga logged, for the requests whose source is not instrumented yet.
   * Suppressed when a source already reported *this* failure a moment ago, so one failure is not
   * counted twice — the source report is the richer of the two and always arrives first.
   *
   * @param {string} tag      the saga name `logError` was called with
   * @param {Object} payload  the error payload; anything else is ignored
   * @returns {boolean} whether anything was reported
   */
  static reportSagaError(tag, payload) {
    try {
      if (deps === null || unloading === true) {
        return false;
      }
      if (payload === null || typeof payload !== 'object') {
        return false;
      }
      const reason = classify(payload.status, payload.retryAfter);
      if (reason === null) {
        return false;
      }

      // Only the failure the source just reported is a duplicate of it. A plain time window
      // would silence every other endpoint for two seconds, exactly during the broad outage
      // where this channel is the only cover the uninstrumented sources have. The payloads
      // that name no endpoint keep the time window alone — there is nothing to tell apart.
      const path = typeof payload.requestURL === 'string' ? normalizePath(payload.requestURL, deps.apiHost) : null;
      if (lastApiReport !== null && Date.now() - lastApiReport.at < SAGA_SUPPRESS_MS && lastApiReport.reason === reason && lastApiReport.status === payload.status && (path === null || lastApiReport.path === path)) {
        return false;
      }
      if (reason === TechnicalErrorReasons.NETWORK && navigator.onLine === false) {
        return false;
      }
      emit({
        channel: TechnicalErrorChannels.SAGA,
        reason,
        method: '-',
        path: tag,
        status: payload.status,
        type: payload.type,
        retryAfter: payload.retryAfter
      });
      return true;
    } catch (ex) {
      return false;
    }
  }

  /**
   * Drops every wired dependency and the accumulated state. Tests only.
   */
  static reset() {
    deps = null;
    sampled = false;
    rollbarBudget = 0;
    unloading = false;
    lastApiReport = null;
    for (const key in gaSeenAt) {
      delete gaSeenAt[key];
    }
    for (const key in rollbarSeen) {
      delete rollbarSeen[key];
    }
  }
}
export { TechnicalErrorReportService };